import React, { useState, useEffect, useContext, useMemo, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API, AuthContext, VirtualTeacherContext } from '../App';
import { Button } from '../components/ui/button';
import { Play, Clock, Users, Star, Headphones, FileText, Calendar, User, ChevronRight, Download, Heart, Eye, Mic, BookOpen, PlayCircle, ChevronDown, Phone, Search, ArrowRight, Sparkles, Trophy, GraduationCap, Atom, Video } from 'lucide-react';
import { useOwnedContent } from '../hooks/useOwnedContent';
import { BoughtBadge } from '../components/BoughtBadge';
import Navbar from '../components/Navbar';
import { useLanguage } from '../context/LanguageContext';
import { Progress } from '../components/ui/progress';
import { useCategories } from '../hooks/useCategories';
import { parseEAT } from '../utils/eatTime';

// Fisher-Yates shuffle — returns a new shuffled copy so each visit/login
// surfaces a different set of teachers on the Home page.
const shuffleArray = (arr) => {
  if (!Array.isArray(arr)) return [];
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
};

function Home() {
  const { user } = useContext(AuthContext);
  const { enabled: vtEnabled } = useContext(VirtualTeacherContext);
  const { t } = useLanguage();
  const { levels: dynamicLevels, subjects: dynamicSubjects } = useCategories();
  const navigate = useNavigate();
  const isBought = useOwnedContent();
  const [courses, setCourses] = useState([]);
  const [liveClasses, setLiveClasses] = useState([]);
  const [exams, setExams] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [ebooks, setEbooks] = useState([]);
  const [simulations, setSimulations] = useState([]);
  const [audioRooms, setAudioRooms] = useState([]);
  const [notes, setNotes] = useState([]);
  const [reels, setReels] = useState([]);
  const [topPerformers, setTopPerformers] = useState([]);
  const [examLeaderboards, setExamLeaderboards] = useState([]);
  const [forYou, setForYou] = useState([]);
  const [forYouSchoolName, setForYouSchoolName] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState('All');
  const [selectedLevel, setSelectedLevel] = useState('All');
  const [settings, setSettings] = useState(null);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  
  const [heroSlide, setHeroSlide] = useState(0);
  // Real-time "N live now" pill — populated from the Redis-backed presence
  // snapshot and refreshed every 20 s. Zero-latency for teachers/students
  // scrolling the home page.
  const [liveNowCount, setLiveNowCount] = useState(0);

  // Continue Learning state
  const [continueContent, setContinueContent] = useState({
    continue_bookings: [],
    continue_reading: [],
    continue_listening: [],
    continue_watching: [],
    continue_exams: [],
    continue_live: [],
    continue_notes: []
  });

  const subjects = ['All', ...dynamicSubjects.slice(0, 7)];
  const levels = ['All', ...dynamicLevels];

  const billingEnabled = settings?.billingEnabledWeb !== false && settings?.billingEnabled !== false;
  
  const formatPrice = useCallback((price) => {
    if (!billingEnabled || !price || price === 0) return 'Free';
    return `TSh ${price?.toLocaleString() || 0}`;
  }, [billingEnabled]);

  // Calculate live progress percentage from start time and duration
  const calcLiveProgress = useCallback((item) => {
    const now = new Date();
    const dateStr = item.scheduled_date || item.date || item.exam_date || '';
    const timeStr = item.scheduled_time || item.time || item.start_time || '00:00';
    let start;
    if (dateStr && timeStr) {
      start = parseEAT(dateStr, timeStr);
    } else if (dateStr) {
      start = new Date(dateStr);
    } else if (item.started_at) {
      start = new Date(item.started_at);
    } else {
      return 5; // just started fallback
    }
    if (!start || isNaN(start.getTime())) return 5;
    const durationMs = (item.duration_minutes || item.duration || 60) * 60 * 1000;
    const elapsed = now - start;
    if (elapsed <= 0) return 2;
    return Math.min(98, Math.max(2, (elapsed / durationMs) * 100));
  }, []);

  // Sort: ongoing/live first → nearest upcoming → future → completed
  const sortByLiveness = useCallback((items) => {
    const now = new Date();
    const isFinished = (s) => ['completed', 'ended', 'cancelled', 'expired'].includes((s || '').toLowerCase());
    return [...items].sort((a, b) => {
      // Finished sessions are never live, regardless of stale is_live flags.
      const aLive = !isFinished(a.status) && (a.status === 'ongoing' || a.status === 'live' || a.is_live);
      const bLive = !isFinished(b.status) && (b.status === 'ongoing' || b.status === 'live' || b.is_live);
      if (aLive && !bLive) return -1;
      if (!aLive && bLive) return 1;
      const aDate = new Date(a.scheduled_date || a.date || a.exam_date || a.start_time || a.created_at || 0);
      const bDate = new Date(b.scheduled_date || b.date || b.exam_date || b.start_time || b.created_at || 0);
      const aUpcoming = aDate >= now;
      const bUpcoming = bDate >= now;
      if (aUpcoming && !bUpcoming) return -1;
      if (!aUpcoming && bUpcoming) return 1;
      if (aUpcoming && bUpcoming) return aDate - bDate; // nearest first
      return bDate - aDate; // most recent completed first
    });
  }, []);

  const loadCourses = useCallback(() => {
    let url = `${API}/courses?limit=12`;
    if (selectedLevel !== 'All') {
      url += `&level=${selectedLevel}`;
    }
    axios.get(url).then(res => {
      let filteredCourses = res.data;
      if (selectedSubject !== 'All') {
        filteredCourses = filteredCourses.filter(c => 
          c.title.toLowerCase().includes(selectedSubject.toLowerCase())
        );
      }
      setCourses(filteredCourses);
    });
  }, [selectedLevel, selectedSubject]);

  useEffect(() => {
    axios.get(`${API}/settings`).then(res => {
      setSettings(res.data);
      setSettingsLoaded(true);
    }).catch(() => setSettingsLoaded(true));
    
    // Single aggregate endpoint replaces 8+ individual API calls
    // Pass student_level for personalized content sorting
    const levelParam = user?.student_level ? `?student_level=${encodeURIComponent(user.student_level)}` : '';
    axios.get(`${API}/landing-data${levelParam}`).then(res => {
      const d = res.data;
      setCourses(d.courses || []);
      setLiveClasses(sortByLiveness(d.live_classes || []));
      setExams(sortByLiveness(d.exams || []));
      setTeachers(shuffleArray(d.teachers || []));
      setEbooks(d.ebooks || []);
      setAudioRooms(sortByLiveness(d.audio_rooms || []));
      setNotes(d.notes || []);
      setReels(d.reels || []);
      setDataLoaded(true);
    }).catch(() => {
      // Fallback to individual calls if aggregate fails
      loadCourses();
      Promise.all([
        axios.get(`${API}/live-classes?limit=8`),
        axios.get(`${API}/exams`),
        axios.get(`${API}/teachers?limit=8`),
        axios.get(`${API}/ebooks?limit=8`),
        axios.get(`${API}/audio-rooms`),
        axios.get(`${API}/notes`),
        axios.get(`${API}/reels?limit=8`)
      ]).then(([liveRes, examsRes, teachersRes, ebooksRes, audioRes, notesRes, reelsRes]) => {
        setLiveClasses(sortByLiveness((liveRes.data || []).filter(lc => lc.status !== 'to_be_rescheduled' && lc.status !== 'expired')));
        setExams(sortByLiveness(examsRes.data));
        setTeachers(shuffleArray(teachersRes.data));
        setEbooks(ebooksRes.data);
        setAudioRooms(sortByLiveness(audioRes.data));
        setNotes(notesRes.data);
        setReels(reelsRes.data);
        setDataLoaded(true);
      }).catch(() => setDataLoaded(true));
    });

    // Load PhET simulations (separate call, non-blocking)
    axios.get(`${API}/phet/simulations?science_only=true&limit=12`).then(res => setSimulations(res.data || [])).catch(() => {});

    // Load top performers for leaderboard slide
    axios.get(`${API}/exams/top-performers`).then(res => {
      const examData = res.data || [];
      // Store raw grouped data for per-subject leaderboards
      setExamLeaderboards(examData);
      // Flatten: extract top students from all exams and sort by best percentage
      const allStudents = examData.flatMap(exam =>
        (exam.top_students || []).map(s => ({ ...s, exam_title: exam.exam_title }))
      ).filter(s => s.percentage > 0).sort((a, b) => b.percentage - a.percentage).slice(0, 5);
      setTopPerformers(allStudents);
    }).catch(() => {});
    
    // Load continue reading/watching content if user is logged in
    const token = localStorage.getItem('token');
    if (token) {
      axios.get(`${API}/analytics/continue-content`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => {
        setContinueContent(res.data);
      }).catch(() => {});

      // Load personalized "For You" mixed recommendations
      axios.get(`${API}/recommendations/for-you?limit=12`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => {
        setForYou(res.data?.recommendations || []);
        setForYouSchoolName(res.data?.school_name || null);
      }).catch(() => {});
    }
  }, [loadCourses, user?.student_level]);

  useEffect(() => {
    loadCourses();
  }, [loadCourses]);

  // Hero slide auto-rotation is handled by the dynamic carousel useEffect

  // Hero slide auto-rotation is handled by the dynamic carousel useEffect

  const orderedSections = useMemo(() => {
    const defaultSections = [
      { id: 'hero', name: 'Hero Section', enabled: true, order: 0, platform: 'both' },
      { id: 'filters', name: 'Quick Filters', enabled: true, order: 1, platform: 'both' },
      { id: 'for_you', name: 'For You', enabled: true, order: 1.5, platform: 'both' },
      { id: 'homework', name: 'Homework', enabled: true, order: 1.7, platform: 'both' },
      { id: 'courses', name: 'Courses', enabled: true, order: 2, platform: 'both' },
      { id: 'live_classes', name: 'Live Classes', enabled: true, order: 3, platform: 'both' },
      { id: 'teachers', name: 'Featured Teachers', enabled: true, order: 4, platform: 'both' },
      { id: 'ebooks', name: 'eBooks', enabled: true, order: 5, platform: 'both' },
      { id: 'simulations', name: 'Science Simulations', enabled: true, order: 5.5, platform: 'both' },
      { id: 'exams', name: 'Practice Exams', enabled: true, order: 6, platform: 'both' },
      { id: 'audio_rooms', name: 'Audio Rooms', enabled: true, order: 7, platform: 'both' },
      { id: 'notes', name: 'Study Notes & Past Papers', enabled: true, order: 8, platform: 'both' },
      { id: 'reels', name: 'Reels', enabled: true, order: 9, platform: 'both' },
      { id: 'teacher_banner', name: 'Teacher Recruitment', enabled: true, order: 3.5, platform: 'both' },
    ];
    
    if (!settings?.layoutOrder) return defaultSections;
    const isNative = typeof window !== 'undefined' && window.ReactNativeWebView;
    const platform = isNative ? 'mobile' : 'web';
    const layout = [...settings.layoutOrder];
    // Inject "For You" if not in the stored layout (new section)
    if (!layout.find(s => s.id === 'for_you')) {
      const filtersIdx = layout.findIndex(s => s.id === 'filters');
      const coursesIdx = layout.findIndex(s => s.id === 'courses');
      const insertOrder = filtersIdx >= 0 && coursesIdx >= 0
        ? (layout[filtersIdx].order + layout[coursesIdx].order) / 2
        : 1.5;
      layout.push({ id: 'for_you', name: 'For You', enabled: true, order: insertOrder, platform: 'both' });
    }
    // Inject Simulations if not in stored layout
    if (!layout.find(s => s.id === 'simulations')) {
      const ebooksIdx = layout.findIndex(s => s.id === 'ebooks');
      const examsIdx = layout.findIndex(s => s.id === 'exams');
      const simOrder = ebooksIdx >= 0 && examsIdx >= 0
        ? (layout[ebooksIdx].order + layout[examsIdx].order) / 2
        : 5.5;
      layout.push({ id: 'simulations', name: 'Science Simulations', enabled: true, order: simOrder, platform: 'both' });
    }
    return layout
      .sort((a, b) => a.order - b.order)
      .filter(s => {
        if (!s.platform || s.platform === 'both') return true;
        return s.platform === platform;
      });
  }, [settings]);

  const featuredContent = settings?.heroFeaturedContent || [];

  // Build dynamic hero slides from real content
  const heroSlides = useMemo(() => {
    if (featuredContent.length > 0) return featuredContent.map(fc => ({ ...fc, slide_type: fc.content_type || fc.slide_type || 'course' }));
    const slides = [];
    if (liveClasses.length > 0) {
      const lc = liveClasses[0];
      slides.push({ slide_type: 'live_class', title: lc.title, description: `Live with ${lc.teacher_name || 'Expert Teacher'}`, link: `/live-classes/${lc.id}`, thumbnail: lc.thumbnail, tag: 'LIVE CLASS', cta: 'Join Now', accent: 'red' });
    }
    if (topPerformers.length > 0) {
      slides.push({ slide_type: 'leaderboard', title: 'Top Students This Week', description: 'Compete with the best minds & earn your rank!', link: '/exams', tag: 'LEADERBOARD', cta: 'View Rankings', accent: 'amber' });
    }
    if (ebooks.length > 0) {
      const eb = ebooks[0];
      slides.push({ slide_type: 'ebook', title: eb.title, description: eb.description || `by ${eb.author || 'Expert Author'}`, link: `/ebooks/${eb.id}`, thumbnail: eb.cover_image || eb.thumbnail, tag: 'eBOOK', cta: 'Read Now', accent: 'emerald' });
    }
    if (courses.length > 0) {
      const c = courses[0];
      slides.push({ slide_type: 'course', title: c.title, description: c.description || `by ${c.teacher_name || 'Expert Teacher'}`, link: `/courses/${c.id}`, thumbnail: c.thumbnail, tag: 'COURSE', cta: 'Enroll Now', accent: 'blue' });
    }
    if (teachers.length > 0) {
      const t = teachers[0];
      slides.push({ slide_type: 'teacher', title: `Learn 1-on-1 with ${t.name}`, description: t.bio || `${t.subjects?.[0] || 'Expert'} teacher available for private sessions`, link: `/teachers/${t.id}`, thumbnail: t.avatar, tag: '1-ON-1 TUTOR', cta: 'Book Session', accent: 'violet' });
    }
    if (exams.length > 0) {
      slides.push({ slide_type: 'exam', title: 'Practice & Compete', description: `${exams.length} exams ready. Challenge yourself & rank up!`, link: '/exams', tag: 'EXAMS', cta: 'Start Now', accent: 'purple' });
    }
    if (audioRooms.length > 0) {
      slides.push({ slide_type: 'audio', title: 'Live Audio Rooms', description: `${audioRooms.length} study rooms active. Join discussions & Q&A`, link: '/audio-rooms', tag: 'AUDIO ROOM', cta: 'Listen In', accent: 'teal' });
    }
    return slides;
  }, [featuredContent, liveClasses, topPerformers, ebooks, courses, teachers, exams, audioRooms]);

  // Auto-rotate hero slides
  useEffect(() => {
    if (heroSlides.length <= 1) return;
    const timer = setInterval(() => setHeroSlide(prev => (prev + 1) % heroSlides.length), 5000);
    return () => clearInterval(timer);
  }, [heroSlides.length]);

  // Poll the presence snapshot so the "N live now" pill next to the
  // Live Classes header updates in near-real-time as teachers connect.
  // Also fires a toast the FIRST time a class the user is enrolled in
  // (or, if unauthenticated, any class) shows up as live.
  const seenLiveRoomsRef = useRef(new Set());
  const firstPresencePollRef = useRef(true);
  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL;
    if (!API) return undefined;
    let cancelled = false;
    const refresh = async () => {
      try {
        const res = await fetch(`${API}/api/rtc/presence/live`);
        if (!res.ok) return;
        const json = await res.json();
        if (cancelled) return;
        setLiveNowCount(json.active_rooms || 0);
        // Detect NEW rooms since the last poll → toast the enrolled class.
        const activeRoomIds = new Set((json.rooms || []).map(r => r.room_id));
        if (!firstPresencePollRef.current) {
          activeRoomIds.forEach((rid) => {
            if (seenLiveRoomsRef.current.has(rid)) return;
            const lc = liveClasses.find(c => c.id === rid || `live_${c.id}` === rid);
            if (!lc) return;
            const teacher = lc.teacher_name || 'A teacher';
            toast.success(`🔴 ${teacher} just started "${lc.title}"`, {
              duration: 10000,
              action: {
                label: 'Join',
                onClick: () => navigate(`/live-classes/${lc.id}`),
              },
            });
          });
        }
        seenLiveRoomsRef.current = activeRoomIds;
        firstPresencePollRef.current = false;
      } catch (_) { /* offline — just skip this tick */ }
    };
    refresh();
    const t = setInterval(refresh, 20000);
    return () => { cancelled = true; clearInterval(t); };
  }, [liveClasses, navigate]);

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="home-page">
      <Navbar />
      
      {/* Render sections based on layout order */}
      {orderedSections && (
        orderedSections.filter(s => s.enabled).map(section => {
          switch (section.id) {
            case 'hero':
              const currentSlide = heroSlides[heroSlide % Math.max(heroSlides.length, 1)] || {};
              const accentMap = { red: 'bg-red-500', amber: 'bg-amber-500', emerald: 'bg-emerald-500', blue: 'bg-indigo-500', violet: 'bg-violet-500', purple: 'bg-purple-500', teal: 'bg-teal-500' };
              const slideAccent = accentMap[currentSlide.accent] || 'bg-indigo-500';
              return (
                <React.Fragment key="hero-and-continue">
                  <section className="bg-gradient-to-br from-indigo-600 via-indigo-700 to-violet-700 text-white" data-testid="hero-section">
                  <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-10 md:py-16">
                    <div className="flex flex-col-reverse md:flex-row md:items-center gap-8">
                      {/* Left: Branding + CTA (bottom on mobile) */}
                      <div className="md:w-2/5 flex flex-col">
                        <h1 className="text-3xl md:text-4xl lg:text-5xl font-black mb-3 tracking-tight" data-testid="hero-title">
                          {settings?.appName || 'Darasani'}
                        </h1>
                        <p className="text-sm md:text-base mb-6 text-indigo-200 leading-relaxed max-w-md">
                          {settings?.tagline || 'Access world-class education from expert teachers.'}
                        </p>
                        <div className="flex gap-3 mb-6">
                          <Button size="default" className="bg-white text-indigo-700 hover:bg-indigo-50 h-10 px-6 rounded-full font-bold text-sm shadow-lg" data-testid="get-started-btn" asChild>
                            <Link to={user ? "/dashboard" : "/login"}>Get Started</Link>
                          </Button>
                          <Button size="default" variant="outline" className="border-white/40 text-white hover:bg-white/10 h-10 px-6 rounded-full font-semibold text-sm" data-testid="explore-courses-btn" asChild>
                            <Link to="/all-courses">Explore</Link>
                          </Button>
                        </div>
                        {/* Stats row */}
                        <div className="flex gap-6">
                          <div><span className="text-white font-black text-xl">{courses.length + liveClasses.length}</span><span className="text-indigo-300 block text-[11px]">Classes</span></div>
                          <div><span className="text-white font-black text-xl">{teachers.length}</span><span className="text-indigo-300 block text-[11px]">Teachers</span></div>
                          <div><span className="text-white font-black text-xl">{ebooks.length}</span><span className="text-indigo-300 block text-[11px]">eBooks</span></div>
                          <div><span className="text-white font-black text-xl">{exams.length}</span><span className="text-indigo-300 block text-[11px]">Exams</span></div>
                        </div>
                      </div>

                      {/* Right: Dynamic Content Carousel (top on mobile) */}
                      <div className="md:w-3/5" data-testid="hero-carousel">
                        {heroSlides.length > 0 ? (
                          <div className="relative bg-white/10 backdrop-blur-sm rounded-2xl overflow-hidden border border-white/10">
                            {heroSlides.map((slide, idx) => (
                              <div key={idx} className={`transition-opacity duration-700 ${idx === heroSlide % heroSlides.length ? 'block' : 'hidden'}`}>
                                {/* Slide content */}
                                {slide.slide_type === 'leaderboard' ? (
                                  <div className="p-5 md:p-6">
                                    <div className="flex items-center gap-2 mb-4">
                                      <span className="bg-amber-500/30 text-amber-200 text-[10px] font-bold px-2.5 py-1 rounded-full flex items-center gap-1">
                                        <Trophy className="w-3 h-3" /> LEADERBOARD
                                      </span>
                                    </div>
                                    <h3 className="text-xl md:text-2xl font-black text-white mb-1">Top Students This Week</h3>
                                    <p className="text-xs text-indigo-200/80 mb-4">Compete with the best minds & earn your rank!</p>
                                    <div className="space-y-2 mb-4">
                                      {topPerformers.slice(0, 3).map((p, pIdx) => (
                                        <div key={pIdx} className="flex items-center gap-3 bg-white/10 rounded-xl px-3 py-2">
                                          <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black ${pIdx === 0 ? 'bg-yellow-400 text-yellow-900' : pIdx === 1 ? 'bg-gray-300 text-gray-700' : 'bg-amber-600 text-amber-100'}`}>{pIdx + 1}</span>
                                          <img loading="lazy" src={p.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(p.student_name)}&size=28`} alt="" className="w-7 h-7 rounded-full" />
                                          <span className="text-sm font-semibold text-white/90 flex-1 truncate">{p.student_name}</span>
                                          <span className="text-sm font-black text-amber-300">{p.percentage}%</span>
                                        </div>
                                      ))}
                                    </div>
                                    <Link to="/exams" className="inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-600 text-white text-sm font-bold px-5 py-2.5 rounded-full transition-colors">
                                      View Rankings <ArrowRight className="w-3.5 h-3.5" />
                                    </Link>
                                  </div>
                                ) : (
                                  <div className="flex flex-col md:flex-row">
                                    {/* Image */}
                                    <div className="md:w-1/2 h-48 md:h-64 relative overflow-hidden">
                                      {slide.thumbnail ? (
                                        <img loading="lazy" src={slide.thumbnail} alt={slide.title} className="w-full h-full object-cover" />
                                      ) : (
                                        <div className={`w-full h-full ${slideAccent} opacity-30 flex items-center justify-center`}>
                                          {slide.slide_type === 'live_class' && <Play className="w-16 h-16 text-white/40" />}
                                          {slide.slide_type === 'ebook' && <BookOpen className="w-16 h-16 text-white/40" />}
                                          {slide.slide_type === 'course' && <BookOpen className="w-16 h-16 text-white/40" />}
                                          {slide.slide_type === 'teacher' && <User className="w-16 h-16 text-white/40" />}
                                          {slide.slide_type === 'exam' && <FileText className="w-16 h-16 text-white/40" />}
                                          {slide.slide_type === 'audio' && <Headphones className="w-16 h-16 text-white/40" />}
                                        </div>
                                      )}
                                      <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/40 md:bg-gradient-to-t md:from-transparent md:to-black/30" />
                                      {slide.slide_type === 'live_class' && <span className="absolute top-3 left-3 bg-red-500 text-white text-[10px] font-bold px-2 py-1 rounded animate-pulse">LIVE</span>}
                                    </div>
                                    {/* Details */}
                                    <div className="md:w-1/2 p-5 md:p-6 flex flex-col justify-center">
                                      <span className={`${slideAccent} text-white text-[10px] font-bold px-2.5 py-1 rounded-full w-fit mb-3`}>
                                        {slide.tag}
                                      </span>
                                      <h3 className="text-lg md:text-xl font-black text-white mb-2 line-clamp-2">{slide.title}</h3>
                                      <p className="text-xs text-indigo-200/80 mb-4 line-clamp-2">{slide.description}</p>
                                      <Link to={slide.link || '#'} className={`inline-flex items-center gap-1.5 bg-white text-indigo-700 text-sm font-bold px-5 py-2.5 rounded-full w-fit hover:bg-indigo-50 transition-colors`} data-testid={`hero-cta-${slide.slide_type}`}>
                                        {slide.cta} <ArrowRight className="w-3.5 h-3.5" />
                                      </Link>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                            {/* Slide indicators */}
                            {heroSlides.length > 1 && (
                              <div className="flex justify-center gap-1.5 pb-4 px-5">
                                {heroSlides.map((s, idx) => (
                                  <button key={idx} onClick={() => setHeroSlide(idx)}
                                    className={`h-1.5 rounded-full transition-all duration-300 ${idx === heroSlide % heroSlides.length ? 'bg-white w-8' : 'bg-white/30 w-3 hover:bg-white/50'}`}
                                    data-testid={`hero-dot-${idx}`} />
                                ))}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="bg-white/10 rounded-2xl h-64 flex items-center justify-center">
                            <p className="text-indigo-200">Loading content...</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </section>
                
                {/* Continue Learning Section - Very Small Tiles */}
                {user && (continueContent.continue_bookings?.length > 0 ||
                          continueContent.continue_reading.length > 0 || 
                          continueContent.continue_watching.length > 0 || 
                          continueContent.continue_listening.length > 0 ||
                          continueContent.continue_exams?.length > 0 ||
                          continueContent.continue_live?.length > 0 ||
                          continueContent.continue_notes?.length > 0) && (
                  <section className="py-3 bg-white border-b border-slate-100" data-testid="continue-section">
                    <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
                      <div className="flex items-center gap-2 mb-2">
                        <PlayCircle className="w-4 h-4 text-indigo-500" />
                        <h2 className="text-sm font-bold text-slate-700">Continue Learning</h2>
                      </div>
                      <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
                        {/* 1-on-1 Sessions — always first */}
                        {(continueContent.continue_bookings || []).map((item) => {
                          const isLive = item.content?.is_live || item.content?.status === 'in_progress';
                          return (
                            <div key={`booking-${item.content.id}`} onClick={() => navigate(`/session/${item.content.id}`)}
                              className={`flex-shrink-0 flex items-center gap-2 rounded-lg px-2.5 py-2 cursor-pointer group border ${isLive ? 'bg-red-50 border-red-200 ring-1 ring-red-400/30' : 'bg-purple-50 border-purple-100 hover:bg-purple-100'}`}
                              data-testid={`continue-booking-${item.content.id}`}>
                              <div className={`w-8 h-8 rounded flex items-center justify-center shrink-0 ${isLive ? 'bg-red-100' : 'bg-purple-100'}`}>
                                <Video className={`w-3.5 h-3.5 ${isLive ? 'text-red-600' : 'text-purple-600'}`} />
                              </div>
                              <div className="min-w-0 max-w-[140px]">
                                <p className="text-[11px] font-semibold text-slate-700 truncate flex items-center gap-1">
                                  {item.content.title}
                                  {isLive && <span className="inline-block w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />}
                                </p>
                                <p className="text-[9px] text-slate-500 mt-0.5 truncate">
                                  {isLive ? 'LIVE NOW' : `1-on-1 · ${item.content.date || ''} ${item.content.start_time || ''}`}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                        {continueContent.continue_watching.map((item) => (
                          <div key={`watch-${item.content.id}`} onClick={() => navigate(`/learn/course/${item.content.id}${item.last_lesson_id ? `?lesson=${item.last_lesson_id}` : ''}`)}
                            className="flex-shrink-0 flex items-center gap-2 bg-slate-50 rounded-lg px-2.5 py-2 hover:bg-slate-100 cursor-pointer group border border-slate-100" data-testid={`continue-watch-${item.content.id}`}>
                            <div className="w-8 h-8 rounded bg-indigo-100 flex items-center justify-center shrink-0">
                              <Play className="w-3.5 h-3.5 text-indigo-600" />
                            </div>
                            <div className="min-w-0 max-w-[120px]">
                              <p className="text-[11px] font-semibold text-slate-700 truncate">{item.content.title}</p>
                              <div className="flex items-center gap-1.5 mt-0.5">
                                <Progress value={item.progress_percent} className="h-1 flex-1" />
                                <span className="text-[9px] text-slate-400 shrink-0">{item.progress_percent}%</span>
                              </div>
                            </div>
                          </div>
                        ))}
                        {continueContent.continue_reading.map((item) => (
                          <div key={`read-${item.content.id}`} onClick={() => navigate(`/ebooks/${item.content.id}`)}
                            className="flex-shrink-0 flex items-center gap-2 bg-slate-50 rounded-lg px-2.5 py-2 hover:bg-slate-100 cursor-pointer group border border-slate-100" data-testid={`continue-read-${item.content.id}`}>
                            <div className="w-8 h-8 rounded bg-emerald-100 flex items-center justify-center shrink-0">
                              <BookOpen className="w-3.5 h-3.5 text-emerald-600" />
                            </div>
                            <div className="min-w-0 max-w-[120px]">
                              <p className="text-[11px] font-semibold text-slate-700 truncate">{item.content.title}</p>
                              <div className="flex items-center gap-1.5 mt-0.5">
                                <Progress value={item.progress_percent} className="h-1 flex-1" />
                                <span className="text-[9px] text-slate-400 shrink-0">p.{item.current_page}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                        {continueContent.continue_listening.map((item) => (
                          <div key={`listen-${item.content.id}`} onClick={() => navigate(`/ebooks/${item.content.id}`)}
                            className="flex-shrink-0 flex items-center gap-2 bg-slate-50 rounded-lg px-2.5 py-2 hover:bg-slate-100 cursor-pointer group border border-slate-100" data-testid={`continue-listen-${item.content.id}`}>
                            <div className="w-8 h-8 rounded bg-purple-100 flex items-center justify-center shrink-0">
                              <Headphones className="w-3.5 h-3.5 text-purple-600" />
                            </div>
                            <div className="min-w-0 max-w-[120px]">
                              <p className="text-[11px] font-semibold text-slate-700 truncate">{item.content.title}</p>
                              <div className="flex items-center gap-1.5 mt-0.5">
                                <Progress value={item.progress_percent} className="h-1 flex-1" />
                                <span className="text-[9px] text-slate-400 shrink-0">{item.progress_percent}%</span>
                              </div>
                            </div>
                          </div>
                        ))}
                        {(continueContent.continue_exams || []).map((item) => (
                          <div key={`exam-${item.content.id}`} onClick={() => navigate(`/exams/${item.content.id}`)}
                            className="flex-shrink-0 flex items-center gap-2 bg-slate-50 rounded-lg px-2.5 py-2 hover:bg-slate-100 cursor-pointer group border border-slate-100" data-testid={`continue-exam-${item.content.id}`}>
                            <div className="w-8 h-8 rounded bg-amber-100 flex items-center justify-center shrink-0">
                              <FileText className="w-3.5 h-3.5 text-amber-600" />
                            </div>
                            <div className="min-w-0 max-w-[120px]">
                              <p className="text-[11px] font-semibold text-slate-700 truncate">{item.content.title}</p>
                              <p className="text-[9px] text-slate-400 mt-0.5">{item.answered}/{item.total_questions} done</p>
                            </div>
                          </div>
                        ))}
                        {(continueContent.continue_live || []).map((item) => (
                          <div key={`live-${item.content.id}`} onClick={() => navigate(`/live-classes/${item.content.id}`)}
                            className="flex-shrink-0 flex items-center gap-2 bg-slate-50 rounded-lg px-2.5 py-2 hover:bg-slate-100 cursor-pointer group border border-slate-100" data-testid={`continue-live-${item.content.id}`}>
                            <div className="w-8 h-8 rounded bg-red-100 flex items-center justify-center shrink-0">
                              <Play className="w-3.5 h-3.5 text-red-600" />
                            </div>
                            <div className="min-w-0 max-w-[120px]">
                              <p className="text-[11px] font-semibold text-slate-700 truncate">{item.content.title}</p>
                              <p className="text-[9px] text-slate-400 mt-0.5">Enrolled</p>
                            </div>
                          </div>
                        ))}
                        {(continueContent.continue_notes || []).map((item) => (
                          <div key={`note-${item.content.id}`} onClick={() => navigate(`/notes/${item.content.id}`)}
                            className="flex-shrink-0 flex items-center gap-2 bg-slate-50 rounded-lg px-2.5 py-2 hover:bg-slate-100 cursor-pointer group border border-slate-100" data-testid={`continue-note-${item.content.id}`}>
                            <div className="w-8 h-8 rounded bg-teal-100 flex items-center justify-center shrink-0">
                              <FileText className="w-3.5 h-3.5 text-teal-600" />
                            </div>
                            <div className="min-w-0 max-w-[120px]">
                              <p className="text-[11px] font-semibold text-slate-700 truncate">{item.content.title}</p>
                              <div className="flex items-center gap-1.5 mt-0.5">
                                <Progress value={item.progress_percent} className="h-1 flex-1" />
                                <span className="text-[9px] text-slate-400 shrink-0">{item.progress_percent}%</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </section>
                )}
                </React.Fragment>
              );

            case 'filters':
              return (
                <section key="filters" className="bg-white border-b border-slate-200 sticky top-16 z-40 shadow-sm" data-testid="filters-section">
                  <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-3">
                    <div className="flex items-center gap-3">
                      {/* Level Dropdown */}
                      <div className="relative flex-shrink-0" data-testid="level-filter-dropdown">
                        <select
                          value={selectedLevel}
                          onChange={(e) => setSelectedLevel(e.target.value)}
                          className="appearance-none bg-indigo-600 text-white pl-4 pr-9 py-2 rounded-full font-semibold text-sm cursor-pointer shadow-lg shadow-indigo-600/20 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                          data-testid="filter-level-select"
                        >
                          {levels.map(level => (
                            <option key={level} value={level} className="text-slate-900 bg-white">{level === 'All' ? 'All Levels' : level}</option>
                          ))}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white pointer-events-none" />
                      </div>

                      {/* Subject Filter Pills */}
                      <div className="flex gap-2 filter-pills-row flex-1 min-w-0">
                        {subjects.map(subject => (
                          <button key={subject} onClick={() => setSelectedSubject(subject)} className={`px-5 py-2 rounded-full font-semibold text-sm whitespace-nowrap transition-all ${selectedSubject === subject ? 'bg-teal-600 text-white shadow-lg shadow-teal-600/30' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-testid={`filter-subject-${subject.toLowerCase()}`}>{subject}</button>
                        ))}
                      </div>
                    </div>
                  </div>
                </section>
              );

            case 'for_you':
              if (!user || forYou.length === 0) return null;
              return (
                <section key="for_you" className="py-8 md:py-10 bg-gradient-to-r from-slate-50 to-indigo-50/50" data-testid="for-you-section">
                  <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
                    <div className="flex justify-between items-center mb-4">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-md shadow-indigo-500/20">
                          <Sparkles className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <h2 className="text-xl md:text-2xl font-black" data-testid="for-you-title">For You</h2>
                          <p className="text-slate-500 text-xs">{forYouSchoolName && user.student_level ? `Tailored for ${forYouSchoolName} ${user.student_level}` : user.student_level ? `Tailored for ${user.student_level}` : 'Recommended content'}</p>
                        </div>
                      </div>
                      <Link to="/for-you" className="text-sm text-indigo-600 font-semibold flex items-center gap-1 hover:text-indigo-700" data-testid="for-you-see-all">
                        See All <ChevronRight className="w-4 h-4" />
                      </Link>
                    </div>
                    <div className="flex gap-4 overflow-x-auto pb-3 scrollbar-hide snap-x snap-mandatory">
                      {forYou.map(item => {
                        const typeConfig = {
                          course: { badge: 'Course', color: 'bg-blue-500', link: `/courses/${item.id}`, icon: BookOpen },
                          ebook: { badge: 'eBook', color: 'bg-emerald-500', link: `/ebooks/${item.id}`, icon: BookOpen },
                          exam: { badge: 'Exam', color: 'bg-purple-500', link: `/exams/${item.id}`, icon: FileText },
                          live_class: { badge: 'Live Class', color: 'bg-red-500', link: `/live-classes/${item.id}`, icon: Play },
                          audio_room: { badge: 'Audio Room', color: 'bg-teal-500', link: `/audio-rooms?join=${item.id}`, icon: Headphones },
                          note: { badge: 'Notes', color: 'bg-amber-500', link: `/notes/${item.id}`, icon: FileText },
                        };
                        const cfg = typeConfig[item.content_type] || typeConfig.course;
                        const ItemIcon = cfg.icon;
                        const thumb = item.thumbnail || item.cover_image;
                        return (
                          <Link
                            key={`fy-${item.content_type}-${item.id}`}
                            to={cfg.link}
                            className="group bg-white rounded-xl overflow-hidden border border-slate-200 hover:shadow-lg hover:border-indigo-200 transition-all duration-300 hover:-translate-y-0.5 flex-shrink-0 w-56 snap-start"
                            data-testid={`for-you-card-${item.id}`}
                          >
                            <div className="relative h-32">
                              {thumb ? (
                                <img loading="lazy" src={thumb} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                              ) : (
                                <div className="w-full h-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                                  <ItemIcon className="w-10 h-10 text-slate-300" />
                                </div>
                              )}
                              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                              <span className={`absolute top-2 left-2 ${cfg.color} text-white text-[10px] font-bold px-2 py-0.5 rounded-full`}>{cfg.badge}</span>
                              {item.is_school_content && (
                                <span className="absolute top-2 left-[4.5rem] bg-amber-400 text-amber-900 text-[10px] font-bold px-1.5 py-0.5 rounded-full flex items-center gap-0.5" data-testid={`school-badge-${item.id}`}>
                                  <GraduationCap className="w-3 h-3" /> School
                                </span>
                              )}
                              {item.level && !item.is_school_content && (
                                <span className="absolute top-2 right-2 bg-white/90 text-slate-700 text-[10px] font-semibold px-1.5 py-0.5 rounded">{item.level}</span>
                              )}
                            </div>
                            <div className="p-3">
                              <h3 className="text-sm font-bold mb-1 line-clamp-2 group-hover:text-indigo-600 transition-colors">{item.title}</h3>
                              {(item.content_type === 'live_class' || item.content_type === 'audio_room') && (item.date || item.scheduled_date || item.scheduled_time) && (
                                <div className="flex items-center gap-1 text-[11px] text-slate-400 mb-1">
                                  <Calendar className="w-3 h-3" />
                                  <span>
                                    {item.date || item.scheduled_date || ''}
                                    {(item.time || item.start_time || item.scheduled_time) ? `, ${item.time || item.start_time || item.scheduled_time}` : ''}
                                  </span>
                                </div>
                              )}
                              <div className="flex items-center justify-between text-xs text-slate-500">
                                <span>{item.subject || item.category || ''}</span>
                                {isBought(item.content_type, item) ? (
                                  <BoughtBadge testId={`feed-bought-${item.id}`} />
                                ) : item.price > 0 ? (
                                  <span className="font-bold text-teal-600">TSh {item.price?.toLocaleString()}</span>
                                ) : (
                                  <span className="font-semibold text-green-600">Free</span>
                                )}
                              </div>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </section>
              );

            case 'homework':
              if (!user || !forYouSchoolName) return null;
              const homeworkItems = forYou.filter(i => i.school_category === 'homework');
              if (homeworkItems.length === 0) return null;
              return (
                <section key="homework" className="py-6 md:py-8 bg-gradient-to-r from-orange-50 to-amber-50/50" data-testid="homework-section">
                  <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
                    <div className="flex justify-between items-center mb-4">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center shadow-md shadow-orange-500/20">
                          <FileText className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <h2 className="text-xl md:text-2xl font-black" data-testid="homework-title">Homework</h2>
                          <p className="text-slate-500 text-xs">Assignments from {forYouSchoolName}</p>
                        </div>
                      </div>
                      <Link to="/for-you" className="text-sm text-orange-600 font-semibold flex items-center gap-1 hover:text-orange-700" data-testid="homework-see-all">
                        See All <ChevronRight className="w-4 h-4" />
                      </Link>
                    </div>
                    <div className="flex gap-4 overflow-x-auto pb-3 scrollbar-hide snap-x snap-mandatory">
                      {homeworkItems.map(item => {
                        const typeConfig = {
                          course: { badge: 'Course', color: 'bg-blue-500', link: `/courses/${item.id}`, icon: BookOpen },
                          ebook: { badge: 'eBook', color: 'bg-emerald-500', link: `/ebooks/${item.id}`, icon: BookOpen },
                          exam: { badge: 'Exam', color: 'bg-purple-500', link: `/exams/${item.id}`, icon: FileText },
                          live_class: { badge: 'Live', color: 'bg-red-500', link: `/live-classes/${item.id}`, icon: Play },
                          audio_room: { badge: 'Audio', color: 'bg-teal-500', link: `/audio-rooms?join=${item.id}`, icon: Headphones },
                          note: { badge: 'Notes', color: 'bg-amber-500', link: `/notes/${item.id}`, icon: FileText },
                        };
                        const cfg = typeConfig[item.content_type] || typeConfig.course;
                        const ItemIcon = cfg.icon;
                        const thumb = item.thumbnail || item.cover_image;
                        return (
                          <Link key={`hw-${item.content_type}-${item.id}`} to={cfg.link} className="group bg-white rounded-xl overflow-hidden border border-orange-200 hover:shadow-lg hover:border-orange-300 transition-all duration-300 hover:-translate-y-0.5 flex-shrink-0 w-56 snap-start" data-testid={`homework-card-${item.id}`}>
                            <div className="relative h-28">
                              {thumb ? <img loading="lazy" src={thumb} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" /> :
                                <div className="w-full h-full bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center"><ItemIcon className="w-8 h-8 text-orange-300" /></div>
                              }
                              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                              <span className={`absolute top-2 left-2 ${cfg.color} text-white text-[10px] font-bold px-2 py-0.5 rounded-full`}>{cfg.badge}</span>
                              <span className="absolute top-2 right-2 bg-orange-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">Homework</span>
                            </div>
                            <div className="p-3">
                              <h3 className="text-sm font-bold mb-1 line-clamp-2 group-hover:text-orange-600 transition-colors">{item.title}</h3>
                              <div className="flex items-center justify-between text-xs text-slate-500">
                                <span>{item.subject || ''}</span>
                                {item.level && <span className="text-orange-500 font-medium">{item.level}</span>}
                              </div>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </section>
              );

            case 'courses':
              return (
                <React.Fragment key="courses">
                <section className="py-8 md:py-12" data-testid="courses-section">
                  <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
                    <div className="flex justify-between items-center mb-4">
                      <div>
                        <h2 className="text-xl md:text-2xl font-black mb-0.5" data-testid="classes-on-demand-title">Classes on Demand</h2>
                        <p className="text-slate-600 text-sm">{user?.student_level ? `Prioritized for ${user.student_level}` : 'High-quality recorded classes'}</p>
                      </div>
                      <Button variant="ghost" className="text-teal-600 font-semibold shrink-0" data-testid="view-all-classes-btn" asChild>
                        <Link to="/all-courses">See More <ChevronRight className="w-4 h-4 ml-1" /></Link>
                      </Button>
                    </div>
                    <div className="flex gap-4 overflow-x-auto pb-3 scrollbar-hide snap-x snap-mandatory">
                      {(user?.student_level ? courses : [...courses].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))).slice(0, 8).map(course => (
                        <Link key={course.id} to={`/courses/${course.id}`} className="group bg-white rounded-xl overflow-hidden border border-slate-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex-shrink-0 w-64 snap-start" data-testid={`class-card-${course.id}`}>
                          <div className="relative overflow-hidden h-36">
                            <img loading="lazy" src={course.thumbnail} alt={course.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-transparent" />
                            {billingEnabled && <div className="absolute top-2 left-2 bg-amber-500 text-black px-2 py-0.5 rounded-full text-xs font-bold flex items-center gap-1"><Star className="w-3 h-3 fill-current" />Best Seller</div>}
                          </div>
                          <div className="p-3">
                            <div className="flex items-center gap-2 mb-1.5">
                              <span className="bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full text-xs font-bold">{course.level}</span>
                              <span className="bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full text-xs font-bold">{course.subject}</span>
                              {course.recordings_count > 0 && (
                                <span className="inline-flex items-center gap-0.5 bg-gradient-to-r from-teal-600 to-emerald-600 text-white px-2 py-0.5 rounded-full text-xs font-bold" data-testid={`course-recordings-badge-${course.id}`}>
                                  <Video className="w-3 h-3" />{course.recordings_count}
                                </span>
                              )}
                            </div>
                            <h3 className="text-sm font-bold mb-1 line-clamp-2 group-hover:text-teal-600 transition-colors">{course.title}</h3>
                            <p className="text-xs text-slate-600 mb-2 flex items-center gap-1"><User className="w-3 h-3" />{course.teacher_name}</p>
                            <div className="flex items-center justify-between">
                              {isBought('course', course) ? <BoughtBadge testId={`course-bought-${course.id}`} /> : (
                                <span className={`text-sm font-black ${billingEnabled ? 'text-teal-600' : 'text-green-600'}`} data-testid={`course-price-${course.id}`}>{formatPrice(course.price)}</span>
                              )}
                              <div className="flex items-center gap-1 text-xs text-slate-500"><Star className="w-3 h-3 text-amber-500 fill-amber-500" />{course.rating}</div>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </section>

                {/* Virtual Tutor banner — hidden for now (2026-02-15) per user request. */}
                {false && vtEnabled && (
                  <section className="py-4 md:py-5" data-testid="virtual-teacher-banner-section">
                    <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
                      <div className="bg-gradient-to-r from-violet-600 to-fuchsia-600 rounded-xl px-5 py-4 md:px-8 md:py-5 flex flex-col md:flex-row items-center gap-4 md:gap-6 text-white">
                        <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center shrink-0">
                          <Sparkles className="w-5 h-5" />
                        </div>
                        <div className="flex-1 text-center md:text-left">
                          <h3 className="font-bold text-base md:text-lg">Meet our Virtual Tutor</h3>
                          <p className="text-violet-100 text-xs md:text-sm">On-demand NECTA lessons taught by Mwalimu Asha — pick any topic, get a video lesson + quiz in seconds.</p>
                        </div>
                        <Button size="sm" className="bg-white text-violet-600 hover:bg-violet-50 h-9 px-5 rounded-full font-bold text-sm shadow shrink-0" asChild data-testid="virtual-tutor-banner-btn">
                          <Link to="/tutor">Try Virtual Tutor</Link>
                        </Button>
                      </div>
                    </div>
                  </section>
                )}
                </React.Fragment>
              );

            case 'live_classes':
              return (
                <section key="live_classes" className="py-8 md:py-12 bg-white" data-testid="live-classes-section">
                  <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
                    <div className="flex justify-between items-center mb-4">
                      <div>
                        <h2 className="text-xl md:text-2xl font-black mb-0.5 flex items-center gap-2" data-testid="live-classes-section-title">
                          Live Classes
                          {liveNowCount > 0 && (
                            <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-red-600 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full" data-testid="live-now-pill">
                              <span className="relative flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                              </span>
                              {liveNowCount} live now
                            </span>
                          )}
                        </h2>
                        <p className="text-slate-600 text-sm">Interactive sessions with expert teachers</p>
                      </div>
                      <Button variant="ghost" className="text-teal-600 font-semibold shrink-0" asChild><Link to="/live-classes">See More <ChevronRight className="w-4 h-4 ml-1" /></Link></Button>
                    </div>
                    <div className="flex gap-4 overflow-x-auto pb-3 scrollbar-hide snap-x snap-mandatory">
                      {(user?.student_level ? liveClasses : liveClasses).slice(0, 8).map(lc => {
                        const isFinishedLc = ['completed','ended','cancelled','expired'].includes((lc.status || '').toLowerCase());
                        const isOngoing = !isFinishedLc && (lc.status === 'ongoing' || lc.status === 'live' || lc.is_live);
                        const isRescheduled = lc.status === 'to_be_rescheduled';
                        const isCompleted = lc.status === 'completed' || lc.status === 'ended';
                        const lcDate = lc.date || (lc.scheduled_time ? lc.scheduled_time.split('T')[0] : '');
                        const lcTime = lc.time || lc.start_time || (lc.scheduled_time ? lc.scheduled_time.split('T')[1]?.substring(0, 5) : '');
                        const formattedDate = lcDate ? new Date(lcDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
                        // Detect past scheduled classes that lingered without a status update
                        const scheduledAt = (() => {
                          if (lc.scheduled_time && lc.scheduled_time.includes('T')) return new Date(lc.scheduled_time);
                          if (lcDate && lcTime) return new Date(`${lcDate}T${lcTime}`);
                          if (lcDate) return new Date(`${lcDate}T23:59:59`);
                          return null;
                        })();
                        const isPastMissed = !isOngoing && !isCompleted && !isRescheduled && scheduledAt && scheduledAt < new Date();
                        return (
                        <Link key={lc.id} to={`/live-classes/${lc.id}`} className="group bg-slate-50 rounded-xl overflow-hidden border border-slate-200 hover:shadow-lg transition-all flex-shrink-0 w-60 snap-start">
                          <div className="relative h-32">
                            <img loading="lazy" src={lc.thumbnail} alt={lc.title} className="w-full h-full object-cover" />
                            {isOngoing ? (
                              <div className="absolute top-2 left-2 flex items-center gap-1.5 bg-red-600 text-white px-2.5 py-1 rounded-full text-[10px] font-extrabold shadow-lg" style={{ animation: 'pulse 1.5s ease-in-out infinite' }}>
                                <span className="w-2 h-2 bg-white rounded-full animate-ping" style={{ animationDuration: '1s' }} />
                                <span className="relative">LIVE NOW</span>
                              </div>
                            ) : isCompleted ? (
                              <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-xs font-bold bg-green-600 text-white">Completed</div>
                            ) : isRescheduled ? (
                              <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-xs font-bold bg-orange-500 text-white">To Be Rescheduled</div>
                            ) : isPastMissed ? (
                              <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-xs font-bold bg-slate-600 text-white">Ended</div>
                            ) : (
                              <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-xs font-bold bg-amber-500 text-black">Upcoming</div>
                            )}
                            {/* Red progress bar based on actual start time */}
                            {isOngoing && (
                              <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-red-900/50">
                                <div className="h-full bg-red-500 rounded-r-full shadow-[0_0_8px_rgba(239,68,68,0.6)]" style={{ width: `${calcLiveProgress(lc)}%` }} />
                              </div>
                            )}
                          </div>
                          <div className="p-3">
                            <h3 className="text-sm font-bold mb-1 line-clamp-2">{lc.title}</h3>
                            <p className="text-xs text-slate-500 mb-1">{lc.teacher_name}</p>
                            {(lc.level || lc.subject) && (
                              <div className="flex flex-wrap gap-1 mb-1.5">
                                {lc.level && <span className="bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded text-[10px] font-semibold">{lc.level}</span>}
                                {lc.subject && <span className="bg-teal-100 text-teal-700 px-1.5 py-0.5 rounded text-[10px] font-semibold">{lc.subject}</span>}
                              </div>
                            )}
                            <div className="flex items-center justify-between text-xs">
                              {(formattedDate || lcTime) ? (
                                <span className="text-slate-500"><Calendar className="w-3 h-3 inline mr-1" />{formattedDate}{lcTime ? ` ${lcTime}` : ''}</span>
                              ) : (
                                <span className="text-slate-400 text-[10px]">Date TBD</span>
                              )}
                              <span className={`font-bold ${billingEnabled ? 'text-teal-600' : 'text-green-600'}`}>{formatPrice(lc.price)}</span>
                            </div>
                          </div>
                        </Link>
                        );
                      })}
                    </div>
                  </div>
                </section>
              );

            case 'teachers':
              return (
                <section key="teachers" className="py-8 md:py-12" data-testid="teachers-section">
                  <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
                    <div className="flex justify-between items-center mb-4">
                      <div>
                        <h2 className="text-xl md:text-2xl font-black mb-0.5">Expert Teachers</h2>
                        <p className="text-slate-600 text-sm">Learn from the best educators</p>
                      </div>
                      <Button variant="ghost" className="text-teal-600 font-semibold shrink-0" asChild><Link to="/teachers">See More <ChevronRight className="w-4 h-4 ml-1" /></Link></Button>
                    </div>
                    <div className="flex gap-5 overflow-x-auto pb-3 scrollbar-hide snap-x snap-mandatory">
                      {teachers.slice(0, 8).map(teacher => (
                        <Link key={teacher.id} to={`/teachers/${teacher.id}`} className="group text-center flex-shrink-0 w-40 md:w-auto snap-start" data-testid={`teacher-card-${teacher.id}`}>
                          {/* Circular avatar without border - Facebook style */}
                          <div className="relative w-24 h-24 mx-auto mb-3">
                            <img loading="lazy" src={teacher.avatar} alt={teacher.name} className="w-24 h-24 rounded-full object-cover shadow-lg group-hover:shadow-xl transition-shadow" />
                            {/* Status indicator circle: Green (available), Red (engaged), Gray (away) */}
                            <div className="absolute bottom-1 right-1" data-testid={`teacher-status-${teacher.id}`}>
                              {teacher.is_live_teaching || teacher.availability_status === 'engaged' ? (
                                <div className="w-5 h-5 bg-red-500 rounded-full border-3 border-white shadow-md" title="Engaged"></div>
                              ) : teacher.is_online || teacher.availability_status === 'available' ? (
                                <div className="w-5 h-5 bg-green-500 rounded-full border-3 border-white shadow-md" title="Available"></div>
                              ) : (
                                <div className="w-5 h-5 bg-gray-400 rounded-full border-3 border-white shadow-md" title="Away"></div>
                              )}
                            </div>
                          </div>
                          <h3 className="font-bold text-sm group-hover:text-teal-600 transition-colors">{teacher.name}</h3>
                          <p className="text-xs text-slate-500 mb-0.5">{teacher.subjects?.slice(0, 2).join(', ')}</p>
                          {teacher.preferred_levels?.length > 0 && (
                            <p className="text-[10px] text-teal-600 font-medium mb-0.5">{teacher.preferred_levels.slice(0, 3).join(', ')}</p>
                          )}
                          <div className="flex items-center justify-center gap-2 text-xs text-slate-500">
                            <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {teacher.followers}</span>
                            {(teacher.is_live_teaching || teacher.availability_status === 'engaged') && (
                              <span className="text-red-500 font-semibold">Engaged</span>
                            )}
                            {!teacher.is_live_teaching && teacher.availability_status !== 'engaged' && (teacher.is_online || teacher.availability_status === 'available') && (
                              <span className="text-green-500 font-semibold">Available</span>
                            )}
                          </div>
                          {billingEnabled && <p className="text-xs text-teal-600 font-bold mt-1">TSh {teacher.rate_per_hour?.toLocaleString()}/hr</p>}
                        </Link>
                      ))}
                    </div>
                  </div>
                </section>
              );

            case 'ebooks':
              return (
                <section key="ebooks" className="py-8 md:py-12 bg-white" data-testid="ebooks-section">
                  <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
                    <div className="flex justify-between items-center mb-4">
                      <div>
                        <h2 className="text-xl md:text-2xl font-black mb-0.5">eBooks & Study Materials</h2>
                        <p className="text-slate-600 text-sm">Study materials for offline reading</p>
                      </div>
                      <Button variant="ghost" className="text-teal-600 font-semibold shrink-0" asChild><Link to="/ebooks">See More <ChevronRight className="w-4 h-4 ml-1" /></Link></Button>
                    </div>
                    <div className="flex gap-4 overflow-x-auto pb-3 scrollbar-hide snap-x snap-mandatory">
                      {(user?.student_level ? ebooks : [...ebooks].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))).slice(0, 10).map(ebook => (
                        <Link key={ebook.id} to={`/ebooks/${ebook.id}`} className="group bg-slate-50 rounded-xl overflow-hidden border border-slate-200 hover:shadow-lg transition-all flex-shrink-0 w-36 snap-start">
                          <div className="relative aspect-[3/4]">
                            <img loading="lazy" src={ebook.cover_image} alt={ebook.title} className="w-full h-full object-cover" />
                            {(ebook.audio_enabled || ebook.has_audio) && (
                              <div
                                className="absolute top-2 right-2 bg-violet-600 text-white px-1.5 py-0.5 rounded-full shadow-lg flex items-center gap-0.5 text-[9px] font-bold"
                                title="Available as audiobook"
                                data-testid={`audiobook-badge-${ebook.id}`}
                              >
                                <Headphones className="w-2.5 h-2.5" /> Audio
                              </div>
                            )}
                          </div>
                          <div className="p-2.5">
                            <h3 className="text-xs font-bold mb-1 line-clamp-2">{ebook.title}</h3>
                            <p className="text-xs text-slate-500 mb-0.5">{ebook.author}</p>
                            {(ebook.level || ebook.subject) && (
                              <div className="flex flex-wrap gap-0.5 mb-1">
                                {ebook.level && <span className="bg-indigo-100 text-indigo-700 px-1 py-0 rounded text-[9px] font-semibold">{ebook.level}</span>}
                                {ebook.subject && <span className="bg-teal-100 text-teal-700 px-1 py-0 rounded text-[9px] font-semibold">{ebook.subject}</span>}
                              </div>
                            )}
                            <div className="flex items-center justify-between">
                              {isBought('ebook', ebook) ? <BoughtBadge testId={`ebook-bought-${ebook.id}`} /> : (
                                <span className={`text-xs font-bold ${billingEnabled && ebook.price > 0 ? 'text-teal-600' : 'text-green-600'}`}>{ebook.price === 0 || !billingEnabled ? 'Free' : formatPrice(ebook.price)}</span>
                              )}
                              <Download className="w-3 h-3 text-slate-400" />
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </section>
              );

            case 'simulations':
              return (
                <section key="simulations" className="py-8 md:py-12 bg-gradient-to-r from-purple-50 to-indigo-50" data-testid="simulations-section">
                  <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
                    <div className="flex justify-between items-center mb-4">
                      <div className="flex items-center gap-2">
                        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                          <Atom className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <h2 className="text-xl md:text-2xl font-black mb-0.5" data-testid="simulations-title">Science Simulations</h2>
                          <p className="text-slate-600 text-sm">Interactive PhET simulations</p>
                        </div>
                      </div>
                      <Button variant="ghost" className="text-indigo-600 font-semibold shrink-0" asChild>
                        <Link to="/simulations">See More <ChevronRight className="w-4 h-4 ml-1" /></Link>
                      </Button>
                    </div>
                    <div className="flex gap-4 overflow-x-auto pb-3 scrollbar-hide snap-x snap-mandatory">
                      {simulations.length > 0 ? simulations.map(sim => (
                        <Link key={sim.id} to={`/simulations?sim=${sim.name}`}
                          className="group bg-white rounded-xl overflow-hidden border border-slate-200 hover:border-indigo-400 hover:shadow-lg transition-all flex-shrink-0 w-44 snap-start"
                          data-testid={`sim-home-${sim.name}`}>
                          <div className="aspect-[4/3] bg-slate-100 relative overflow-hidden">
                            <img loading="lazy" src={sim.image} alt={sim.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              onError={e => { e.target.src = sim.thumbnail; }} />
                          </div>
                          <div className="p-2.5">
                            <h3 className="text-xs font-bold mb-1 line-clamp-2 leading-tight">{sim.title}</h3>
                            <div className="flex flex-wrap gap-0.5">
                              {sim.subjects.slice(0, 2).map(sub => (
                                <span key={sub} className="text-[9px] px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 font-medium">{sub}</span>
                              ))}
                            </div>
                          </div>
                        </Link>
                      )) : Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="bg-white rounded-xl overflow-hidden border border-slate-200 flex-shrink-0 w-44 snap-start animate-pulse">
                          <div className="aspect-[4/3] bg-slate-200" />
                          <div className="p-2.5 space-y-2"><div className="h-3 bg-slate-200 rounded w-3/4" /><div className="h-2 bg-slate-200 rounded w-1/2" /></div>
                        </div>
                      ))}
                    </div>
                  </div>
                </section>
              );

            case 'exams':
              return (
                <React.Fragment key="exams-with-leaderboard">
                {/* Per-Subject Leaderboard Banner */}
                <LeaderboardBanner topPerformers={topPerformers} examLeaderboards={examLeaderboards} />

                <section className="py-8 md:py-12" data-testid="exams-section">
                  <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
                    <div className="flex justify-between items-center mb-4">
                      <div>
                        <h2 className="text-xl md:text-2xl font-black mb-0.5">Practice Exams</h2>
                        <p className="text-slate-600 text-sm">Test your knowledge with quizzes and exams</p>
                      </div>
                      <Button variant="ghost" className="text-teal-600 font-semibold shrink-0" asChild><Link to="/exams">See More <ChevronRight className="w-4 h-4 ml-1" /></Link></Button>
                    </div>
                    <div className="flex gap-4 overflow-x-auto pb-3 scrollbar-hide snap-x snap-mandatory">
                      {(user?.student_level ? exams : exams).slice(0, 6).map(exam => {
                        const examType = exam.exam_type || exam.type || 'exam';
                        const duration = exam.duration_minutes || exam.duration || 60;
                        const now = new Date();
                        const examDate = exam.exam_date ? new Date(exam.exam_date) : null;
                        const durationMs = duration * 60 * 1000;
                        const examEnd = examDate ? new Date(examDate.getTime() + durationMs) : null;
                        const hoursUntil = examDate ? (examDate - now) / (1000 * 60 * 60) : null;
                        const isOngoing = examDate && now >= examDate && now <= examEnd;
                        const isUpcoming = examDate && hoursUntil > 0 && hoursUntil <= 12;
                        const isTimed = duration > 0;
                        const typeColors = {
                          quiz: 'bg-green-50 text-green-700 border border-green-200',
                          mock: 'bg-orange-50 text-orange-700 border border-orange-200',
                          exam: 'bg-purple-50 text-purple-700 border border-purple-200',
                          past_paper: 'bg-blue-50 text-blue-700 border border-blue-200',
                        };
                        return (
                          <Link key={exam.id} to={`/exams/${exam.id}`}
                            className="group block bg-white rounded-xl border border-gray-100 hover:shadow-lg hover:shadow-purple-100/80 hover:border-purple-200 transition-all flex-shrink-0 w-72 snap-start"
                            data-testid={`home-exam-card-${exam.id}`}>
                            <div className="p-4">
                              {/* Type + Status badges */}
                              <div className="flex items-center gap-2 mb-2.5 flex-wrap">
                                <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${typeColors[examType] || typeColors.exam}`}>
                                  {examType?.replace('_', ' ')}
                                </span>
                                {isOngoing && (
                                  <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-red-600 text-white shadow-md" style={{ animation: 'pulse 1.5s ease-in-out infinite' }}>
                                    <span className="w-1.5 h-1.5 bg-white rounded-full animate-ping" style={{ animationDuration: '1s' }} />LIVE NOW
                                  </span>
                                )}
                                {isUpcoming && (
                                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200">UPCOMING</span>
                                )}
                                {isTimed && (
                                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-violet-50 text-violet-600 border border-violet-100">
                                    <Clock className="w-3 h-3 inline mr-0.5 -mt-0.5" />Timed
                                  </span>
                                )}
                              </div>
                              {/* Title */}
                              <h3 className="font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-purple-700 transition-colors">{exam.title}</h3>
                              {/* Details */}
                              <div className="flex items-center gap-3 text-sm text-gray-600 mb-2">
                                <span className="flex items-center gap-1"><BookOpen className="w-3.5 h-3.5 text-indigo-400" />{exam.subject}</span>
                                <span className="flex items-center gap-1"><Star className="w-3.5 h-3.5 text-purple-400" />{exam.level}</span>
                              </div>
                              <div className="flex items-center gap-3 text-sm text-gray-600 mb-3">
                                <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-teal-400" />{duration >= 60 ? `${Math.floor(duration/60)}hr${duration%60 ? ` ${duration%60}m` : ''}` : `${duration}min`}</span>
                                {exam.exam_date && (
                                  <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5 text-orange-400" />{new Date(exam.exam_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</span>
                                )}
                              </div>
                              {/* Footer */}
                              <div className="flex items-center justify-between pt-2.5 border-t border-gray-100">
                                <span className="text-xs">
                                  {isBought('exam', exam) ? (
                                    <BoughtBadge testId={`exam-bought-${exam.id}`} />
                                  ) : exam.is_free === false && exam.price > 0 ? (
                                    <span className="font-bold text-orange-600">TSh {exam.price?.toLocaleString()}</span>
                                  ) : (
                                    <span className="font-semibold text-green-600">FREE</span>
                                  )}
                                </span>
                                <span className="text-sm font-semibold text-purple-600 group-hover:text-purple-700 flex items-center gap-1">
                                  Start <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                                </span>
                              </div>
                              {/* Red progress bar for ongoing exams */}
                              {isOngoing && (
                                <div className="mt-2 h-[3px] bg-red-100 rounded-full overflow-hidden">
                                  <div className="h-full bg-red-500 rounded-full shadow-[0_0_8px_rgba(239,68,68,0.5)]" style={{ width: `${calcLiveProgress(exam)}%` }} />
                                </div>
                              )}
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </section>
              </React.Fragment>
              );

            case 'audio_rooms':
              return (
                <section key="audio_rooms" className="py-12 md:py-16 bg-white" data-testid="audio-rooms-section">
                  <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
                    <div className="flex justify-between items-center mb-6">
                      <div>
                        <h2 className="text-2xl md:text-3xl font-black text-slate-800 mb-1">Audio Rooms</h2>
                        <p className="text-slate-500 text-sm">Join live discussions and Q&A sessions</p>
                      </div>
                      <Button variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-50" asChild><Link to="/audio-rooms">View All <ChevronRight className="w-4 h-4 ml-1" /></Link></Button>
                    </div>
                    <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory">
                      {audioRooms.slice(0, 6).map((room, idx) => {
                        const gradients = [
                          'from-rose-50 to-orange-50 border-rose-200/60',
                          'from-sky-50 to-cyan-50 border-sky-200/60',
                          'from-violet-50 to-fuchsia-50 border-violet-200/60',
                          'from-emerald-50 to-teal-50 border-emerald-200/60',
                          'from-amber-50 to-yellow-50 border-amber-200/60',
                          'from-indigo-50 to-blue-50 border-indigo-200/60',
                        ];
                        const accents = [
                          { text: 'text-rose-700', badge: 'bg-rose-100 text-rose-600', sub: 'text-rose-400', bar: 'bg-rose-400' },
                          { text: 'text-sky-700', badge: 'bg-sky-100 text-sky-600', sub: 'text-sky-400', bar: 'bg-sky-400' },
                          { text: 'text-violet-700', badge: 'bg-violet-100 text-violet-600', sub: 'text-violet-400', bar: 'bg-violet-400' },
                          { text: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-600', sub: 'text-emerald-400', bar: 'bg-emerald-400' },
                          { text: 'text-amber-700', badge: 'bg-amber-100 text-amber-600', sub: 'text-amber-400', bar: 'bg-amber-400' },
                          { text: 'text-indigo-700', badge: 'bg-indigo-100 text-indigo-600', sub: 'text-indigo-400', bar: 'bg-indigo-400' },
                        ];
                        const g = gradients[idx % gradients.length];
                        const a = accents[idx % accents.length];
                        return (
                        <Link key={room.id} to={`/audio-rooms?join=${room.id}`}
                          className={`bg-gradient-to-br ${g} rounded-2xl p-5 border flex-shrink-0 w-72 snap-start hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 cursor-pointer`}
                          data-testid={`home-audio-room-${room.id}`}>
                          <div className="flex items-center gap-2 mb-3">
                            {room.is_live && <span className="flex items-center gap-1 bg-red-500 text-white px-2 py-0.5 rounded-full text-[10px] font-extrabold shadow-sm"><span className="w-1.5 h-1.5 bg-white rounded-full animate-ping" style={{ animationDuration: '1s' }} />LIVE</span>}
                            {room.status === 'completed' && <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-[10px] font-bold">Completed</span>}
                            <span className={`text-[10px] font-semibold ${a.badge} px-2 py-0.5 rounded-full`}>{room.category || 'Discussion'}</span>
                          </div>
                          <h3 className={`font-bold text-sm ${a.text} mb-2 line-clamp-2`}>{room.title}</h3>
                          {(room.level || room.subject) && (
                            <div className="flex flex-wrap gap-1 mb-2">
                              {room.level && <span className={`${a.badge} px-1.5 py-0.5 rounded text-[10px] font-semibold`}>{room.level}</span>}
                              {room.subject && <span className={`${a.badge} px-1.5 py-0.5 rounded text-[10px] font-semibold`}>{room.subject}</span>}
                            </div>
                          )}
                          <div className="flex items-center gap-2 mb-3">
                            {room.host_avatar && <img loading="lazy" src={room.host_avatar} alt={room.host_name} className="w-7 h-7 rounded-full border-2 border-white shadow-sm" />}
                            <span className="text-xs text-slate-600 font-medium">{room.host_name}</span>
                          </div>
                          <div className={`flex items-center justify-between text-[11px] ${a.sub}`}>
                            <span><Mic className="w-3 h-3 inline" /> {room.speakers_count || 0} speakers</span>
                            <span><Users className="w-3 h-3 inline" /> {room.listeners_count || 0} listening</span>
                          </div>
                          {(room.scheduled_date || room.date) && (
                            <div className="flex items-center gap-1 text-[10px] text-slate-400 mt-2">
                              <Calendar className="w-3 h-3" />
                              <span>{room.scheduled_date || room.date}{room.scheduled_time ? ` at ${room.scheduled_time}` : room.time ? ` at ${room.time}` : ''}</span>
                            </div>
                          )}
                          {room.is_live && (
                            <div className="mt-3 h-[3px] bg-slate-200 rounded-full overflow-hidden">
                              <div className={`h-full ${a.bar} rounded-full`} style={{ width: `${calcLiveProgress(room)}%` }} />
                            </div>
                          )}
                        </Link>
                        );
                      })}
                    </div>
                  </div>
                </section>
              );

            case 'notes':
              return (
                <section key="notes" className="py-8 md:py-12 bg-white" data-testid="notes-section">
                  <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
                    <div className="flex justify-between items-center mb-6">
                      <div>
                        <h2 className="text-xl md:text-2xl font-black mb-0.5">Study Notes & Past Papers</h2>
                        <p className="text-slate-600 text-sm">Download notes and past examination papers</p>
                      </div>
                      <Button variant="ghost" className="text-teal-600 font-semibold" asChild><Link to="/notes">View All <ChevronRight className="w-4 h-4 ml-1" /></Link></Button>
                    </div>
                    <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory">
                      {notes.slice(0, 6).map(note => {
                        const isPP = note.title?.toLowerCase().includes('past paper');
                        const subjectColors = {
                          Physics: 'from-blue-500 to-cyan-500',
                          Mathematics: 'from-purple-500 to-pink-500',
                          Chemistry: 'from-green-500 to-emerald-500',
                          Biology: 'from-teal-500 to-green-500',
                          English: 'from-red-500 to-rose-500',
                          History: 'from-amber-500 to-orange-500',
                          Geography: 'from-sky-500 to-blue-500',
                          Kiswahili: 'from-indigo-500 to-violet-500',
                        };
                        const subjectColor = subjectColors[note.subject] || 'from-slate-500 to-slate-600';
                        return (
                          <Link key={note.id} to={`/notes/${note.id}`}
                            className="group bg-white rounded-xl border border-gray-100 hover:shadow-lg hover:shadow-indigo-100/80 hover:border-indigo-200 transition-all flex-shrink-0 w-72 snap-start"
                            data-testid={`home-note-card-${note.id}`}>
                            <div className="p-4">
                              {/* Type Badge + NEW */}
                              <div className="flex items-center justify-between mb-2.5">
                                <span className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider ${
                                  isPP ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                                }`}>
                                  {isPP ? 'Past Paper' : 'Study Notes'}
                                </span>
                                {note.is_new && (
                                  <span className="text-[10px] font-bold text-green-600 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full">NEW</span>
                                )}
                                {note.is_updated && (
                                  <span className="text-[10px] font-bold text-blue-600 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-full">UPDATED</span>
                                )}
                              </div>
                              {/* Icon + Title */}
                              <div className="flex items-start gap-3 mb-2">
                                <div className={`w-9 h-9 rounded-lg bg-gradient-to-r ${subjectColor} flex items-center justify-center shrink-0 shadow-sm`}>
                                  {isPP ? <FileText className="w-4 h-4 text-white" /> : <BookOpen className="w-4 h-4 text-white" />}
                                </div>
                                <div className="min-w-0">
                                  <h3 className="font-bold text-sm text-gray-900 mb-0.5 line-clamp-2 group-hover:text-indigo-700 transition-colors">{note.title}</h3>
                                </div>
                              </div>
                              {/* Details */}
                              <div className="flex items-center gap-3 text-xs text-gray-600 mb-2">
                                <span className="flex items-center gap-1"><BookOpen className="w-3 h-3 text-indigo-400" />{note.subject}</span>
                                <span className="flex items-center gap-1"><Star className="w-3 h-3 text-purple-400" />{note.level}</span>
                              </div>
                              {/* Footer */}
                              <div className="flex items-center justify-between pt-2.5 border-t border-gray-100 text-xs">
                                <div className="flex items-center gap-2 text-gray-500">
                                  {note.is_free === false && note.price > 0 ? (
                                    <span className="font-bold text-orange-600">TSh {note.price?.toLocaleString()}</span>
                                  ) : (
                                    <span className="font-semibold text-green-600">FREE</span>
                                  )}
                                  {note.pages > 0 && <span>{note.pages} pg</span>}
                                </div>
                                <span className="text-sm font-semibold text-indigo-600 group-hover:text-indigo-700 flex items-center gap-1">
                                  Read <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                                </span>
                              </div>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </section>
              );

            case 'reels':
              return (
                <section key="reels" className="py-12 md:py-16 bg-slate-50" data-testid="reels-section">
                  <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
                    <div className="flex justify-between items-center mb-6">
                      <div>
                        <h2 className="text-2xl md:text-3xl font-black text-slate-800 mb-1">Quick Reels</h2>
                        <p className="text-slate-500 text-sm">Short educational videos for quick learning</p>
                      </div>
                      <Button variant="outline" className="border-slate-300 text-slate-700 hover:bg-white" asChild><Link to="/reels">View All <ChevronRight className="w-4 h-4 ml-1" /></Link></Button>
                    </div>
                    <div className="flex gap-3 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory">
                      {reels.slice(0, 8).map(reel => (
                        <Link key={reel.id} to={`/reels?id=${reel.id}`} className="group relative rounded-xl overflow-hidden aspect-[9/16] flex-shrink-0 w-36 snap-start">
                          <img loading="lazy" src={reel.thumbnail} alt={reel.title} className="w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                          <div className="absolute bottom-0 left-0 right-0 p-2 text-white">
                            <h3 className="text-xs font-bold mb-1 line-clamp-2">{reel.title}</h3>
                            <div className="flex items-center gap-2 text-xs text-white/80">
                              <span className="flex items-center gap-0.5"><Heart className="w-3 h-3" />{reel.likes >= 1000 ? `${(reel.likes/1000).toFixed(1)}K` : reel.likes}</span>
                              <span className="flex items-center gap-0.5"><Eye className="w-3 h-3" />{reel.views >= 1000 ? `${(reel.views/1000).toFixed(1)}K` : reel.views}</span>
                            </div>
                          </div>
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <div className="w-12 h-12 bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center"><Play className="w-6 h-6 text-white fill-white ml-0.5" /></div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </section>
              );

            case 'teacher_banner':
              return (
                <section key="teacher_banner" className="py-4 md:py-5" data-testid="teacher-banner-section">
                  <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
                    <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl px-5 py-4 md:px-8 md:py-5 flex flex-col md:flex-row items-center gap-4 md:gap-6 text-white">
                      <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center shrink-0">
                        <Users className="w-5 h-5" />
                      </div>
                      <div className="flex-1 text-center md:text-left">
                        <h3 className="font-bold text-base md:text-lg">Join Our Teaching Community</h3>
                        <p className="text-emerald-100 text-xs md:text-sm">Share your knowledge, reach thousands of students & earn on your own schedule</p>
                      </div>
                      <Button size="sm" className="bg-white text-emerald-600 hover:bg-emerald-50 h-9 px-5 rounded-full font-bold text-sm shadow shrink-0" asChild data-testid="join-teacher-btn">
                        <Link to="/teacher-register">Join as Teacher</Link>
                      </Button>
                    </div>
                  </div>
                </section>
              );

            default:
              // Custom Banner sections
              if (section.type === 'banner' && section.banner) {
                const b = section.banner;
                return (
                  <section key={section.id} className={`py-10 md:py-14 bg-gradient-to-r ${b.bg_gradient || 'from-indigo-600 to-purple-600'} text-white`} data-testid={`banner-section-${section.id}`}>
                    <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
                      <div className={`${b.image_url ? 'grid md:grid-cols-2 gap-8 items-center' : 'text-center max-w-2xl mx-auto'}`}>
                        <div>
                          <h2 className="text-2xl md:text-3xl font-black mb-3">{b.title}</h2>
                          {b.description && <p className="text-white/80 text-base md:text-lg mb-5">{b.description}</p>}
                          {b.cta_text && b.cta_link && (
                            <Button size="lg" className="bg-white text-indigo-700 hover:bg-indigo-50 h-11 px-7 rounded-full font-bold shadow-lg" asChild>
                              <Link to={b.cta_link}>{b.cta_text} <ArrowRight className="w-4 h-4 ml-2" /></Link>
                            </Button>
                          )}
                        </div>
                        {b.image_url && (
                          <div className="hidden md:block">
                            <img loading="lazy" src={b.image_url} alt={b.title} className="rounded-2xl shadow-2xl max-h-64 w-full object-cover" />
                          </div>
                        )}
                      </div>
                    </div>
                  </section>
                );
              }
              return null;
          }
        })
      )}

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid md:grid-cols-4 gap-6">
            <div>
              <h3 className="text-lg font-bold mb-3">{settings?.appName || 'Darasani'}</h3>
              <p className="text-slate-400 text-sm">Empowering students across Africa with quality education.</p>
            </div>
            <div>
              <h4 className="font-bold mb-3">Platform</h4>
              <ul className="space-y-1.5 text-slate-400 text-sm">
                <li><Link to="/all-courses" className="hover:text-white">Courses</Link></li>
                <li><Link to="/live-classes" className="hover:text-white">Live Classes</Link></li>
                <li><Link to="/exams" className="hover:text-white">Exams</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-3">Resources</h4>
              <ul className="space-y-1.5 text-slate-400 text-sm">
                <li><Link to="/ebooks" className="hover:text-white">eBooks</Link></li>
                <li><Link to="/notes" className="hover:text-white">Notes</Link></li>
                <li><Link to="/audio-rooms" className="hover:text-white">Audio Rooms</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-3">Support</h4>
              <ul className="space-y-1.5 text-slate-400 text-sm">
                <li><a href="#" className="hover:text-white">Help Center</a></li>
                <li><a href="#" className="hover:text-white">Contact Us</a></li>
              </ul>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-slate-800 text-center text-slate-400 text-sm">
            <p>&copy; 2025 {settings?.appName || 'Darasani'}. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}


// Per-Subject Leaderboard Banner Component
function LeaderboardBanner({ topPerformers, examLeaderboards }) {
  const [activeSubjectIdx, setActiveSubjectIdx] = useState(0);
  const examsWithStudents = (examLeaderboards || []).filter(e => e.top_students && e.top_students.length > 0);

  // Auto-rotate through subjects on the right side
  useEffect(() => {
    if (examsWithStudents.length <= 1) return;
    const timer = setInterval(() => setActiveSubjectIdx(prev => (prev + 1) % examsWithStudents.length), 4000);
    return () => clearInterval(timer);
  }, [examsWithStudents.length]);

  const activeExam = examsWithStudents[activeSubjectIdx % Math.max(examsWithStudents.length, 1)] || null;

  const RANK_STYLES = [
    'bg-gradient-to-r from-yellow-400 to-amber-400 text-amber-900 shadow-lg shadow-yellow-400/30',
    'bg-gradient-to-r from-slate-300 to-gray-300 text-gray-700',
    'bg-gradient-to-r from-amber-600 to-amber-700 text-amber-100',
  ];

  return (
    <section className="py-6 md:py-10" data-testid="leaderboard-banner">
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
        <div className="bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 rounded-2xl overflow-hidden shadow-xl border border-amber-200/50">
          <div className="grid md:grid-cols-5 gap-0">
            {/* Left: Overall Best Performers + CTA */}
            <div className="md:col-span-2 p-6 md:p-8 bg-gradient-to-br from-amber-500 via-yellow-500 to-orange-400">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 rounded-xl bg-amber-900/20 flex items-center justify-center">
                  <Trophy className="w-6 h-6 text-amber-900" />
                </div>
                <div>
                  <span className="text-amber-900/70 font-bold text-[10px] uppercase tracking-wider block">Weekly</span>
                  <span className="text-amber-900 font-black text-lg leading-tight">Best Performers</span>
                </div>
              </div>

              <p className="text-amber-800/70 text-sm mb-5">Overall best performers of the week</p>

              {/* Top 3 Overall */}
              <div className="space-y-2 mb-6">
                {(topPerformers || []).slice(0, 3).map((p, i) => (
                  <div key={i} className="flex items-center gap-3 bg-white/25 backdrop-blur-sm rounded-xl px-3 py-2.5">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-black ${RANK_STYLES[i] || 'bg-white/30 text-amber-900'}`}>
                      {i + 1}
                    </div>
                    <img loading="lazy" src={p.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(p.student_name || 'S')}&size=32&background=random`} alt="" className="w-8 h-8 rounded-full border-2 border-white/50 object-cover" />
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-bold text-amber-900 block truncate">{p.student_name}</span>
                      <span className="text-[10px] text-amber-800/60 truncate block">{p.exam_title}</span>
                    </div>
                    <span className="text-sm font-black text-amber-900 bg-white/30 px-2.5 py-1 rounded-full">{p.percentage}%</span>
                  </div>
                ))}
                {(!topPerformers || topPerformers.length === 0) && (
                  <div className="text-center py-6">
                    <Trophy className="w-10 h-10 text-amber-900/30 mx-auto mb-2" />
                    <p className="text-sm text-amber-900/50 font-medium">Be the first to top the board!</p>
                  </div>
                )}
              </div>

              <Button className="bg-amber-900 hover:bg-amber-950 text-white rounded-full px-6 h-11 font-bold text-sm shadow-lg w-full" asChild data-testid="leaderboard-cta">
                <Link to="/exams">Test Yourself Now <ChevronRight className="w-4 h-4 ml-1" /></Link>
              </Button>
            </div>

            {/* Right: Auto-Rotating Subject Leaderboards */}
            <div className="md:col-span-3 p-5 md:p-6">
              {examsWithStudents.length > 0 ? (
                <>
                  {/* Subject indicator pills */}
                  <div className="flex items-center gap-2 mb-4">
                    <Star className="w-4 h-4 text-amber-500 shrink-0" />
                    <span className="text-slate-800 font-bold text-sm">Top by Subject</span>
                    <div className="flex gap-1 ml-auto">
                      {examsWithStudents.map((_, idx) => (
                        <button key={idx} onClick={() => setActiveSubjectIdx(idx)}
                          className={`w-2 h-2 rounded-full transition-all ${idx === activeSubjectIdx % examsWithStudents.length ? 'bg-amber-500 w-5' : 'bg-amber-200 hover:bg-amber-300'}`}
                          data-testid={`subject-dot-${idx}`} />
                      ))}
                    </div>
                  </div>

                  {/* Active subject card - carousel */}
                  {examsWithStudents.map((exam, idx) => (
                    <div key={exam.exam_id} className={`transition-opacity duration-500 ${idx === activeSubjectIdx % examsWithStudents.length ? 'block' : 'hidden'}`}>
                      {/* Subject badge + exam title */}
                      <div className="mb-3">
                        <span className="bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full">{exam.subject}</span>
                        <p className="text-sm font-bold text-slate-700 mt-2">{exam.exam_title}</p>
                        <p className="text-[10px] text-slate-500">{(exam.type || 'exam').replace('_', ' ')}</p>
                      </div>

                      {/* Students for this subject */}
                      <div className="space-y-2">
                        {(exam.top_students || []).map((s, i) => (
                          <div key={i} className="flex items-center gap-3 bg-white rounded-xl px-3 py-2.5 border border-slate-100 hover:shadow-sm transition-shadow">
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black shrink-0 ${
                              i === 0 ? 'bg-yellow-400 text-yellow-900' :
                              i === 1 ? 'bg-gray-300 text-gray-700' :
                              i === 2 ? 'bg-amber-600 text-white' :
                              'bg-slate-200 text-slate-600'
                            }`}>{i + 1}</div>
                            <img loading="lazy"
                              src={s.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(s.student_name || 'S')}&size=28&background=random`}
                              alt="" className="w-7 h-7 rounded-full border border-slate-200 object-cover shrink-0" />
                            <span className="text-sm font-semibold text-slate-700 flex-1 truncate">{s.student_name}</span>
                            <div className="text-right shrink-0">
                              <span className="text-sm font-black text-amber-600">{s.percentage}%</span>
                              <span className="text-[10px] text-slate-400 block">{s.score}/{s.max_score}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                <div className="text-center py-10">
                  <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-3">
                    <Trophy className="w-8 h-8 text-amber-400" />
                  </div>
                  <p className="text-slate-600 font-semibold mb-1">No exam results yet</p>
                  <p className="text-slate-400 text-sm mb-4">Complete an exam to appear on the leaderboard!</p>
                  <Button className="bg-amber-500 hover:bg-amber-600 text-white rounded-full px-5 text-sm" asChild>
                    <Link to="/exams">Take an Exam Now</Link>
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Home;
